import axios from 'axios';
import environment from '@src/environment';
import appConfig from '@src/app.config.ES6';

export const state = {
  currentUser: getSavedState('auth.currentUser'),
  currentUserTrainerId: getSavedState('auth.currentUserTrainerId'),
  currentTandemUserId: null,
  clientNavGroups: {
    Menud: [
      {
        title: 'Profile',
        options: [
          {
            name: 'userProfile',
            title: 'User Profile',
          },
        ],
      },
    ],
  },
  clientProfileLogo: {
    Menud: 'https://res.cloudinary.com/menud/image/upload/v1560982217/logo_fi9l3p.png',
  },
};

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser;
  },

  isAdmin(state) {
    if (state.currentUser) {
      return !!state.currentUser.user.isAdmin;
    } else {
      return false;
    }
  },
};

export const mutations = {
  setCurrentUser(state, newValue) {
    state.currentUser = newValue;
    saveState('auth.currentUser', newValue);
    setDefaultAuthHeaders(state);
  },
  setCurrentUserTrainerId(state, newValue) {
    state.currentUserTrainerId = newValue;
    saveState('auth.currentUserTrainerId', newValue);
    setDefaultAuthHeaders(state);
  },
  setCurrentTandemUserId(state, newValue) {
    state.currentTandemUserId = newValue;
  },
};

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state);
    dispatch('validate');
  },

  async getMyTrainersId({ commit, dispatch, getters }, token) {
    if (!state.currentUser) return Promise.resolve(null);

    return await axios
      .post(environment.GET_TRAINER_ID, { token })
      .then((res) => {
        const trainerId = res.data.trainerId || '0';
        commit('setCurrentUserTrainerId', trainerId);
        return trainerId;
      })
      .catch((err) => {
        console.log(err);
        const trainerId = '0'; // if accessing menud nutrition data, and we can't identify the trainer... default to org only data.
        commit('setCurrentUserTrainerId', trainerId);
      });
  },

  signUp({ commit, dispatch, getters }, { username, email, password } = {}) {
    return axios.post(environment.SIGNUP_URL, { username, email, password }).then((res) => {
      const accessToken = res.data.accessToken;
      commit('setCurrentUser', accessToken);
      return accessToken;
    });
  },

  // Logs in the current user.
  logIn({ commit, dispatch, getters }, { username, password } = {}) {
    if (getters.loggedIn) return dispatch('validate');

    return axios.post(environment.USER_LOGIN_URL, { username, password }).then((res) => {
      const accessToken = res.data;
      commit('setCurrentUser', accessToken);
      return accessToken;
    });
  },

  resetPassword({ commit, dispatch, getters }, { email } = {}) {
    return axios.post(environment.FORGOT_PASSWORD_URL, { email }).then((res) => res.data);
  },

  // Logs out the current user.
  logOut({ commit }) {
    window.localStorage.removeItem('auth.currentUser');
    window.localStorage.removeItem('auth.currentUserTrainerId');
    commit('setCurrentUser', null);
    commit('setCurrentUserTrainerId', null);
  },

  // This is stubbed out and pending API completion. May need to be refactored.
  async getUserFromTandem({ commit, dispatch, getters }, { id, client }) {
    if (!state.currentUser) return Promise.resolve(null);

    const url = environment.GET_USER_BY_NUTRITION_TOKEN.replace(':nutritionToken', id).replace(':clientName', client.name);
    return await axios
      .get(url)
      .then((res) => {
        const { data: user = {} } = res;
        commit('setCurrentTandemUserId', user._id);

        Leanplum.setUserId(user._id);
        Leanplum.setUserAttributes({ _id: user._id });
      })
      .catch((err) => console.log(err));
  },

  async getUserFromCustomTraining({ commit, dispatch, getters }, token) {
    if (!state.currentUser) return Promise.resolve(null);

    return await axios
      .post(environment.GET_TRAINER_ID, { token })
      .then((res) => {
        const userId = res.data.userId;
        commit('setCurrentTandemUserId', userId);

        Leanplum.setUserId(userId);
        Leanplum.setUserAttributes({ _id: userId });
      })
      .catch((err) => {
        console.log(err);
      });
  },
  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, dispatch, state }) {
    if (!state.currentUser) return Promise.resolve(null);

    if (appConfig.TRAINER_ID_REQUIRED) {
      dispatch('getMyTrainersId', state.currentUser.id);
    }

    return axios
      .post(environment.VALIDATE_AUTH_TOKEN_URL)
      .then(async (response) => {
        const user = response.data.accessToken;
        commit('setCurrentUser', user);
        return user;
      })
      .catch((err) => {
        if (err.response) {
          commit('setCurrentUser', null);
        }
        return null;
      });
  },

  // take an authenticationToken and get's the current user info
  // instead of asking them for username/password.
  autoLogin(ctx, userAccessToken) {
    if (appConfig.TRAINER_ID_REQUIRED) {
      ctx.dispatch('getMyTrainersId', userAccessToken);
    }

    return axios
      .post(environment.VALIDATE_AUTH_TOKEN_URL, '', { headers: { Authorization: userAccessToken } })
      .then((response) => {
        ctx.commit('setCurrentUser', response.data.accessToken);
        const customTrainingRealms = ['pmeoct', 'issa'];
        if (customTrainingRealms.includes(response.data.accessToken.client.name)) {
          ctx.dispatch('getUserFromCustomTraining', response.data.accessToken.id);
        } else {
          ctx.dispatch('getUserFromTandem', response.data.accessToken);
        }
        console.log(response.data.accessToken);

        return response.data.accessToken;
      })
      .catch((err) => {
        if (err.response) {
          ctx.commit('setCurrentUser', null);
        }
        return err;
      });
  },
};

// ===
// Private helpers
// ===

function getSavedState(key) {
  try {
    return JSON.parse(window.localStorage.getItem(key));
  } catch (err) {
    return null;
  }
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state));
}

function setDefaultAuthHeaders(state) {
  axios.defaults.headers.common.Authorization = state.currentUser ? state.currentUser.id : '';

  if (state.currentUserTrainerId) {
    axios.defaults.headers.common.trainerId = state.currentUserTrainerId;
  }
  // axios.defaults.withCredentials = true;

  // axios.defaults.headers.common.clientName = 'Menud';
}
